import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  keyframes,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import { RootState } from 'src/redux/rootReducerType';
import { removeFsrHighlight } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import type { RenterSignatureDoc } from './signatureFilters';

interface SignatureDocRowProps {
  doc: RenterSignatureDoc;
  mode: 'sign' | 'view';
  certId?: number;
}

const SignatureDocRow: FC<SignatureDocRowProps> = ({ doc, mode, certId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const blueGlow = useMemo(
    () =>
      keyframes`
        0%, 100% {
          box-shadow: 0 0 0 0 ${alpha(theme.palette.info.main, 0)};
        }
        50% {
          box-shadow: 0 0 20px 4px ${alpha(theme.palette.info.main, 0.6)};
        }
      `,
    [theme.palette.info.main]
  );

  const isHighlighted = useSelector((state: RootState) =>
    state.newRenterInterface.fsrHighlights.includes(doc.fsrId)
  );

  const handleAnimationEnd = useCallback(() => {
    if (isHighlighted) {
      dispatch(removeFsrHighlight(doc.fsrId));
    }
  }, [isHighlighted, dispatch, doc.fsrId]);

  const effectiveYear = doc.effectiveDate
    ? new Date(doc.effectiveDate).getFullYear().toString()
    : '';

  const categoryLine = [doc.certCategory, effectiveYear]
    .filter(Boolean)
    .join(' ');

  const handleClick = useCallback(() => {
    if (mode === 'sign') {
      navigate(`/renter/signatures/sign/${doc.fsrId}`);
    } else {
      navigate(
        `/renter/signatures/view/${certId ?? doc.certificationId}/doc/${
          doc.docId
        }`
      );
    }
  }, [navigate, mode, doc.fsrId, doc.docId, doc.certificationId, certId]);

  return (
    <Box
      onAnimationEnd={handleAnimationEnd}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'background.paper',
        animation: isHighlighted ? `${blueGlow} 3s ease-in-out 2` : 'none',
        transition: 'border-color 0.3s ease-in-out',
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap>
        {doc.documentTitle}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ mt: 0.5 }}
      >
        <Stack gap={0.25} sx={{ minWidth: 0 }}>
          {doc.propertyName && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {doc.propertyName}
            </Typography>
          )}
          {categoryLine && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {categoryLine}
            </Typography>
          )}
        </Stack>
        <Button
          size="small"
          color="info.dark"
          variant="outlined"
          onClick={handleClick}
          {...(mode === 'view' && {
            startIcon: (
              <GoogleFontIcon name="visibility" sx={{ fontSize: 16 }} />
            ),
          })}
          sx={{ flexShrink: 0, minWidth: 'auto', py: 0.25, px: 1.5 }}
        >
          <span>{mode === 'sign' ? 'Sign' : 'View'}</span>
        </Button>
      </Stack>
    </Box>
  );
};

export default SignatureDocRow;
